// CountUp.js — Número que conta até o valor final quando entra na tela.
// Equivalente ao `CountUp` do react-bits, sobre `motion`.
//
// Usado na faixa de números institucionais da home (títulos publicados,
// autores, áreas). Conta uma vez só; com reduced-motion mostra o número
// final direto, porque contador é enfeite e o dado é o que importa.
//
// REDE DE SEGURANÇA: a contagem roda em requestAnimationFrame. Se o relógio de
// animação não andar (aba em segundo plano, throttle do navegador), o número
// fica preso no zero — e "0+ títulos publicados" numa editora com 198 livros é
// pior do que não ter contador nenhum. Um `setTimeout`, que usa o relógio de
// temporizadores, crava o valor final passada a duração. Mesma lição do
// useRevelar: nada informativo pode depender do relógio de
// animação para existir.
import React, { useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "motion/react";

const CountUp = ({
  to = 0,
  duration = 1.6,
  sufixo = "",
  prefixo = "",
  className = "",
}) => {
  const ref = useRef(null);
  const naTela = useInView(ref, { once: true, amount: 0.4 });
  const reduzirMovimento = useReducedMotion();
  const [valor, setValor] = useState(0);

  // Se o elemento nunca for observado como visível, o valor final entra assim
  // mesmo — o dado importa mais do que a animação dele.
  useEffect(() => {
    const rede = setTimeout(() => setValor((v) => (v === to ? v : to)), (duration + 1) * 1000);
    return () => clearTimeout(rede);
  }, [to, duration]);

  useEffect(() => {
    if (!naTela) return;
    if (reduzirMovimento || !to) { setValor(to); return; }

    let frame;
    const inicio = performance.now();
    // easeOutExpo: sobe rápido e desacelera no fim — a leitura acompanha melhor
    // do que uma contagem linear, que parece travada no começo.
    const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

    const passo = (agora) => {
      const p = Math.min((agora - inicio) / (duration * 1000), 1);
      setValor(Math.round(easeOutExpo(p) * to));
      if (p < 1) frame = requestAnimationFrame(passo);
    };
    frame = requestAnimationFrame(passo);

    // Segunda rede: se os quadros pararem no meio, o número chega ao fim.
    const fim = setTimeout(() => setValor(to), duration * 1000 + 400);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(fim);
    };
  }, [naTela, to, duration, reduzirMovimento]);

  return (
    <span ref={ref} className={className}>
      {prefixo}
      {valor.toLocaleString("pt-BR")}
      {sufixo}
    </span>
  );
};

export default CountUp;
